import React, { useState } from 'react';
import { useCombobox } from 'downshift';

import {
    FetchOrganizationsComponent,
    Organization,
    DatabaseId,
    Country,
    Locale,
    assertValidFunction,
    handleStateChange,
    OrganizationSearchResp,
    InputTextComponent,
} from '@sheerid/jslib';

interface TypeaheadProps {
    id: string;
    onChange: any;
    programId: DatabaseId;
    autoFocus?: boolean;
    isErrored?: boolean;
    countryCode?: Country;
    disabled?: boolean;
    minimumSearchValueLength?: number;
    placeholder?: string;
    value?: Organization;
    openOrgSearchEnabled?: boolean;
    orgSearchUrl?: string;
    orgSearchTags?: string;
    isRequired?: boolean;
    locale?: Locale;
    urlAddSchoolForm?: string;
}

const Typeahead = ({
    onChange,
    programId,
    isErrored,
    countryCode,
    disabled,
    minimumSearchValueLength,
    placeholder,
    value,
    id,
    openOrgSearchEnabled,
    orgSearchUrl,
    orgSearchTags,
    isRequired,
    locale,
    urlAddSchoolForm,
}: TypeaheadProps) => {
    const [inputValue, setInputValue] = useState('');
    const [loadedOrgs, setLoadedOrgs] = useState([]);

    assertValidFunction(onChange);

    const itemToString = (item: Organization) => (item ? item.name : '');

    const {
        isOpen, highlightedIndex, getMenuProps, getItemProps, getInputProps, getComboboxProps,
    } = useCombobox({
        itemToString,
        id,
        items: loadedOrgs,
        onStateChange: (changes) => handleStateChange(changes, onChange, openOrgSearchEnabled),
        // @ts-ignore
        onInputValueChange: ({ inputValue: newInputValue }) => {
            setInputValue(newInputValue);
        },
        selectedItem: value,
        onSelectedItemChange: ({ selectedItem }) => {
            onChange(selectedItem);
        },
    });

    // Maintain same labelledby value as previous versions even though label has "for" attribute
    const comboboxArgs = { 'aria-labelledby': `sid-${id}-label` };

    const {
        'aria-autocomplete': inputPropAriaAutocomplete,
        'aria-controls': inputPropAriaControls,
        'aria-labelledby': inputPropAriaLabelledby,
        autoComplete: inputPropAutoComplete,
        innerRef: inputPropRef,
        onBlur: inputPropOnBlur,
        onChange: inputPropOnChange,
        onKeyDown: inputPropOnKeyDown,
        value: inputPropValue,
    } = getInputProps({ refKey: `sid-${id}` }, { suppressRefError: true });

    return (
        <div className="sid-organization-list" {...getComboboxProps(comboboxArgs)}>
            <InputTextComponent
                aria-autocomplete={inputPropAriaAutocomplete}
                aria-controls={inputPropAriaControls}
                aria-labelledby={inputPropAriaLabelledby}
                ariaDescribedBy={isErrored ? `sid-${id}-error` : ''}
                autoComplete={inputPropAutoComplete}
                id={id}
                isErrored={isErrored}
                className="sid-l-full-width sid-text-input"
                hidePlaceholder
                required={isRequired}
                disabled={disabled}
                placeholder={placeholder}
                refId={inputPropRef}
                onBlur={inputPropOnBlur}
                onChange={inputPropOnChange}
                onKeyDown={inputPropOnKeyDown}
                value={inputPropValue}
            />

            {/* The menu (div) is always mounted. Its child is conditional. Fixes "Downshift error: getMenuProps() was not applied correctly" */}
            <div {...getMenuProps()}>
                {isOpen ? (
                    <FetchOrganizationsComponent
                        countryCode={countryCode}
                        getItemProps={getItemProps}
                        highlightedIndex={highlightedIndex}
                        itemToString={itemToString}
                        minimumSearchValueLength={minimumSearchValueLength}
                        onLoaded={(loadedOrganizations: OrganizationSearchResp) => setLoadedOrgs(loadedOrganizations)}
                        programId={programId}
                        searchValue={inputValue}
                        orgSearchUrl={orgSearchUrl}
                        orgSearchTags={orgSearchTags}
                        locale={locale}
                        urlAddSchoolForm={urlAddSchoolForm}
                        openOrgSearchEnabled={openOrgSearchEnabled}
                    />
                ) : null}
            </div>
        </div>
    );
};

Typeahead.defaultProps = {
    autoFocus: undefined,
    isErrored: false,
    countryCode: '',
    disabled: false,
    minimumSearchValueLength: 1,
    placeholder: '',
    value: undefined,
    openOrgSearchEnabled: false,
    orgSearchUrl: undefined,
    orgSearchTags: undefined,
    isRequired: false,
    locale: undefined,
    urlAddSchoolForm: undefined,
};

export const TypeaheadComponent = Typeahead;
